/**
 * What Log Time is handed when it opens as a copy of an existing entry. [HW-905]
 *
 * The times are not seeded here: `seedLinkedTime` already reads a stored entry
 * into Date / Duration / Start / End off its UTC digits — the entry's AUTHOR's
 * own wall clock, not whoever happens to be viewing it [HW-916] — and it is
 * what Edit Time Entry opens on, so a copy shows the interval the timesheet
 * row prints. A legacy row (null zone, still a real instant until the backfill
 * converts it) is read in the viewer's zone there instead, which is why the
 * zone travels with the seed; the copy itself is a new entry and goes out in
 * the viewer's zone either way. This decides only what travels, and which rows
 * may be copied at all.
 *
 * Seconds do not travel — the three fields are minute-resolution, so a timer's
 * 14:23:47 for 2h 0m 47s copies as 14:23 for 2h. A deliberate loss rather than
 * a limitation: the stored length is right here, and simply is not sent.
 */

import type { TimeEntryItem } from '@hygge/shared';
import { parseDuration, seedLinkedTime } from './linked-time-fields';

/** The entry values Log Time needs to open as a copy. */
export interface DuplicateSeed {
  defaultProjectId: string;
  projectName: string;
  defaultTaskId?: string;
  taskLabel?: string;
  duplicateOf: {
    startedAt: string;
    duration: number;
    description: string | null;
    /** The source's zone, only so a legacy row seeds as the viewer saw it. [HW-916] */
    timezone: string | null;
  };
}

/**
 * The source entry, reshaped into the props Log Time opens on.
 *
 * A task travels with its project or not at all: passing a task id puts the
 * form in the banner mode where the project is stated rather than picked, which
 * is right, because a task belongs to one project and a moving picker would
 * file the hours against another.
 *
 * Whose hours the copy is stays the form's own default — the viewer's own —
 * because this is offered from the Weekly Timesheet, which shows nobody else's
 * entries. Team Time would have to name the source's employee [HW-864].
 */
export function duplicateSeed(entry: TimeEntryItem): DuplicateSeed {
  const seed: DuplicateSeed = {
    defaultProjectId: entry.projectId,
    projectName: entry.project.name,
    duplicateOf: {
      startedAt: entry.startedAt,
      duration: entry.duration,
      description: entry.description,
      timezone: entry.timezone,
    },
  };
  if (entry.task) {
    seed.defaultTaskId = entry.task.id;
    seed.taskLabel = entry.task.key;
  }
  return seed;
}

/**
 * May this entry be copied at all?
 *
 * Two refusals, and the same control is simply not offered for either:
 *
 *  - IT WOULD RUN PAST THE AUTHOR'S MIDNIGHT. [HW-891] forbids that and
 *    enforces it the moment a reader touches Duration or Start; a copy touches
 *    neither, so a crossing source would slip a new one past the rule in
 *    silence. Judged on the entry's own UTC digits, which are the author's
 *    wall clock and not whoever is viewing it [HW-916] — the same digits
 *    [HW-891] itself judges. A legacy row is judged on the viewer's reading
 *    of its instant instead, because that is what its copy is seeded from and
 *    so what would be sent. Such rows exist — the timer writes them [HW-886]
 *    and older ones are still stored. What they should look like at all is
 *    [HW-899]'s question, not this change's.
 *
 *  - IT IS SHORTER THAN A MINUTE. The row is minute-resolution, so such a
 *    source seeds no Duration and its copy cannot be saved: the button would
 *    sit dead over a box that says nothing. The UI cannot make one — the API
 *    accepts a single second and the timer writes them. Near midnight they are
 *    the first case over again (30s at 23:59:47 ends at 00:00:17), so one
 *    refusal covers both readings.
 *
 * The crossing is judged on the SEEDED row, not on `startedAt + duration`,
 * because that is what the copy would actually send: floored to the minute, so
 * an entry ending at 00:00:47 copies as one ending at 00:00 and is allowed.
 *
 * `viewerZone` is for a caller that already resolved it once for the whole
 * list this entry is one row of — the Weekly Timesheet judges every row on
 * every render, so it hands over the zone it already read rather than making
 * each row ask `Intl` again. Left out, `seedLinkedTime` looks it up itself. [HW-916]
 */
export function canDuplicateEntry(
  entry: Pick<TimeEntryItem, 'startedAt' | 'duration' | 'timezone'>,
  viewerZone?: string
): boolean {
  const { state } = seedLinkedTime(entry, viewerZone);
  if (!state.duration) return false;
  const reading = parseDuration(state.duration);
  // A seeded length the grammar refuses can only be 24h or more, which spans at
  // least a whole day: everything `formatDuration` can emit otherwise parses.
  // Reading it as "does not cross" would offer the control on exactly the rows
  // that cross worst — a timer left running over a weekend.
  if (reading.seconds === null) return false;
  const [hours, minutes] = state.start.split(':').map(Number);
  // Up to midnight, not past it: `startWithinDay` settles 22:00 + 4h to
  // 20:00 — 00:00, so ending at midnight is inside the day.
  return hours * 60 + minutes + reading.seconds / 60 <= 24 * 60;
}
